import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { cn } from '../../lib/cn.js';

export type CalendarProps = DayPickerProps;

/**
 * react-day-picker, dressed in the Zuruflex palette. The selected day is the
 * yellow block; today is outlined rather than filled, so the two never
 * compete for attention.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-1', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row',
        month: 'flex flex-col gap-3',
        month_caption: 'flex h-9 items-center justify-center',
        caption_label: 'inline-flex items-center gap-0.5 font-display text-sm font-bold',
        /*
          With `captionLayout="dropdown"` the month and year are real
          <select>s laid transparently over the caption, so the control
          keeps the native picker's keyboard and screen-reader behaviour
          while showing the Zuruflex caption underneath.
        */
        dropdowns: 'flex items-center gap-2',
        dropdown_root: cn(
          'relative inline-flex items-center rounded-lg px-1.5 py-0.5',
          'hover:bg-surface-hover',
        ),
        dropdown: 'absolute inset-0 z-10 w-full cursor-pointer opacity-0',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          'absolute left-1 top-1 inline-flex size-7 items-center justify-center rounded-lg',
          'text-text-muted hover:bg-surface-hover hover:text-text disabled:opacity-30',
        ),
        button_next: cn(
          'absolute right-1 top-1 inline-flex size-7 items-center justify-center rounded-lg',
          'text-text-muted hover:bg-surface-sunken hover:text-text disabled:opacity-30',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-9 text-[11px] font-bold uppercase tracking-wide text-text-muted',
        week: 'mt-1 flex w-full',
        day: 'relative size-9 p-0 text-center text-sm',
        day_button: cn(
          'inline-flex size-9 items-center justify-center rounded-full font-medium transition-colors',
          'hover:bg-accent-soft hover:text-accent-soft-text',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        ),
        selected: '[&>button]:bg-accent [&>button]:text-accent-text [&>button]:font-bold',
        today: '[&>button]:font-bold [&>button]:text-accent-soft-text',
        outside: 'text-text-subtle opacity-60',
        disabled: 'opacity-40 [&>button]:pointer-events-none [&>button]:line-through',
        range_start: 'rounded-l-full bg-accent-soft',
        range_end: 'rounded-r-full bg-accent-soft',
        range_middle:
          'bg-accent-soft first:rounded-l-full last:rounded-r-full [&>button]:rounded-none [&>button]:bg-transparent',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...rest }) => {
          // `down` is the caret beside a caption dropdown; left and right
          // are the month arrows.
          if (orientation === 'left') return <ChevronLeft className="size-4" {...rest} />;
          if (orientation === 'down')
            return <ChevronDown className="size-3.5 opacity-60" {...rest} />;
          return <ChevronRight className="size-4" {...rest} />;
        },
      }}
      {...props}
    />
  );
}
